import BaseSearchForm from "@/components/forms/BaseSearchForm";
import { useLanguage } from "@/contexts/LanguageContext";
import { useResponsiveComponent } from "@/hooks/useResponsiveComponent";
import { getHeroImageUrl } from "@/lib/cloudinary";
import { memo, useMemo } from "react";

const HomeHeader = memo(() => {
  const { t } = useLanguage();
  const { isMobile } = useResponsiveComponent();

  const heroImageData = useMemo(() => {
    const heroImageUrl =
      "https://res.cloudinary.com/dlqntmmhh/image/upload/q_auto/01_11A-APT_Alberca_rgnzuh.jpg";
    const optimizedHeroUrl = getHeroImageUrl(heroImageUrl, isMobile);
    return { heroImageUrl, optimizedHeroUrl };
  }, [isMobile]);

  return (
    <section className="relative pt-24 pb-16 lg:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3 lg:pr-8">
            <h1 className="lg:text-6xl font-bold text-gray-900 leading-tight lg:leading-snug mb-6 text-4xl">
              {t("home.hero.title")}{" "}
              <span className="text-brand-primary">{t("home.hero.highlight")}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-normal">
              {t("home.hero.subtitle")}
            </p>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8 px-[16px] min-h-[280px] md:min-h-[120px]">
              <BaseSearchForm />
            </div>

            <div className="lg:hidden text-center text-sm text-gray-600">
              <div>Solo super anfitriones • Reserva rápida</div>
              <div>Cancelación flexible</div>
            </div>

            <div className="hidden lg:flex items-center space-x-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>{t("home.hero.bullet_one")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-brand-primary rounded-full"></div>
                <span>{t("home.hero.bullet_two")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>{t("home.hero.bullet_three")}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <picture>
                <source
                  media="(max-width: 768px)"
                  srcSet={getHeroImageUrl(heroImageData.heroImageUrl, true)}
                  type="image/webp"
                />
                <source
                  media="(min-width: 769px)"
                  srcSet={getHeroImageUrl(heroImageData.heroImageUrl, false)}
                  type="image/webp"
                />
                <img
                  src={heroImageData.optimizedHeroUrl}
                  alt="Alojamiento en Manzanillo con alberca - Departamento amueblado"
                  className="w-full h-[250px] md:h-[250px] lg:h-[500px] object-cover"
                  loading="eager"
                  width={isMobile ? "700" : "1000"}
                />
              </picture>

              <div className="absolute top-6 left-6 bg-white rounded-xl shadow-lg p-3 flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-brand-primary text-sm font-semibold">★</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {t("home.hero.average_rating")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t("home.hero.reviews_count")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

HomeHeader.displayName = "HomeHeader";

export default HomeHeader;
